import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { OnDestroy } from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, FormGroupDirective, NG_VALUE_ACCESSOR } from '@angular/forms';
import { City } from '../../../../shared/models/search-bar';
import { DataService } from '../../../../shared/data/data.service';
import { GeolocationService } from '../../../../shared/services/geolocation.service';
import { BehaviorSubject, Subscription, debounceTime, Observable, of, from, switchMap } from 'rxjs';

declare const bootstrap: any;

@Component({
  selector: 'app-city-picker',
  standalone: false,
  templateUrl: './city-picker.component.html',
  styleUrl: './city-picker.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CityPickerComponent),
      multi: true
    }
  ]
})
export class CityPickerComponent implements OnInit, ControlValueAccessor, OnDestroy {
  public isLoading: boolean = false;
  @Input() label: string = '';
  @Input() formControlName!: string;
  @Output() citySection: EventEmitter<City> = new EventEmitter<City>();
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropDown') dropDown!: ElementRef<HTMLElement>;
  // Public observable consumers (template) subscribe to this.
  private locationsSubject = new BehaviorSubject<City[] | null>(null);
  locations$: Observable<City[] | null> = this.locationsSubject.asObservable();

  searchInputControl: FormControl = new FormControl('');
  onChange: any = () => { };
  onTouched: any = () => { };

  selectedLocation: City | null = null;
  value: any;

  // Internal helpers
  private subs: Subscription = new Subscription();
  private lastLocations: City[] = [];
  permissionState: string | undefined = undefined;

  private _bodyScrollLock?: {
    scrollY: number;
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
    overflow: string;
  };

  private _dropdownHiddenHandler?: () => void;

  private lockBodyScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (this._bodyScrollLock) return;

    const body = document.body;
    this._bodyScrollLock = {
      scrollY: window.scrollY || window.pageYOffset || 0,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${this._bodyScrollLock.scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (!this._bodyScrollLock) return;

    const body = document.body;
    const { scrollY, position, top, left, right, width, overflow } = this._bodyScrollLock;

    body.style.position = position;
    body.style.top = top;
    body.style.left = left;
    body.style.right = right;
    body.style.width = width;
    body.style.overflow = overflow;

    this._bodyScrollLock = undefined;
    window.scrollTo(0, scrollY);
  }

  private ensureDropdownHiddenListener(): void {
    if (this._dropdownHiddenHandler) return;
    const el = this.dropDown?.nativeElement;
    if (!el) return;

    this._dropdownHiddenHandler = () => {
      this.unlockBodyScroll();
    };

    el.addEventListener('hidden.bs.dropdown', this._dropdownHiddenHandler);
  }

  constructor(
    private readonly controlContainer: ControlContainer,
    private readonly dataService: DataService,
    private readonly geo: GeolocationService,
  ) { }

  ngOnInit(): void {
    setTimeout(() => {
      if (this.value && this.value.length > 0) {
        this.dataService.getCityById(this.value).subscribe(location => {
          this.selectedLocation = location;
          if (this.selectedLocation) {
            this.citySection.emit(this.selectedLocation);
          }
        });
      }
    });

    this.searchInputControl.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((searchTerm: string) => {
      this.searchLocation(searchTerm);
    });

    // Check geolocation permission state so we can offer "Sort by nearest" when appropriate
    if (navigator?.permissions) {
      try {
        navigator.permissions.query({ name: 'geolocation' as any }).then((status: any) => {
          this.permissionState = status.state;
          status.onchange = () => { this.permissionState = status.state; };
        });
      } catch {
        // ignore permission API errors
      }
    }
  }

  addFocus() {
    if (window?.innerWidth <= 767.98) {
      this.lockBodyScroll();
      this.ensureDropdownHiddenListener();
    }
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

 searchLocation(searchTerm: string): void {

  if (!searchTerm || searchTerm.length < 3) {
    this.locationsSubject.next(null);
    return;
  }
  this.isLoading = true;
  console.log(this.permissionState);

  const storedCoords = localStorage.getItem('userCoords');
  let lat: number | undefined;
  let lon: number | undefined;

  if (storedCoords) {
    try {
      const parsed = JSON.parse(storedCoords);
      // GeolocationService stores { t, coords: { lat, lon } }
      if (parsed && parsed.coords) {
        lat = parsed.coords.lat;
        lon = parsed.coords.lon;
      } else {
        // Fallback for older or alternate storage shapes
        lat = parsed.lat ?? parsed.latitude;
        lon = parsed.lon ?? parsed.longitude;
      }
    } catch (e) {
      console.warn('Invalid userCoords in localStorage');
    }
  }

  console.log('Using coords from storage:', lat, lon, storedCoords);

  // If permission is denied and no coords available, send default (0, 0)
  if (this.permissionState === 'denied' && (lat === undefined || lon === undefined)) {
    console.log('Permission denied — using default coords for text-based search');
    lat = 0;
    lon = 0;
  }

  const list$ = this.dataService.getCityList(searchTerm, lat, lon);

  const sub = list$.subscribe(
    (locations: City[]) => {
      this.lastLocations = locations || [];

      // 🔴 SORTING REMOVED — backend order preserved
      this.isLoading = false;
      this.locationsSubject.next(locations);

    },
    (err) => {
      console.warn('Error fetching city list', err);
      this.isLoading = false;
      this.locationsSubject.next(null);
    }
  );

  this.subs.add(sub);
}


  private sortByCoords(list: City[], lat: number, lon: number): City[] {
    return [...list].sort((a, b) => {
      const da = haversineDistance(lat, lon, parseFloat(a.latitude as any), parseFloat(a.longitude as any));
      const db = haversineDistance(lat, lon, parseFloat(b.latitude as any), parseFloat(b.longitude as any));
      return (isFinite(da) ? da : Number.POSITIVE_INFINITY) - (isFinite(db) ? db : Number.POSITIVE_INFINITY);
    });
  }

  // Called by UI when user clicks "Sort by nearest" - this will request coords (prompt if needed)
  requestAndSort(): void {
    this.geo.getCoords(true).subscribe(coords => {
      if (!coords) {
        console.warn('Could not obtain coordinates to sort results');
        return;
      }
      const sorted = this.sortByCoords(this.lastLocations, coords.lat, coords.lon);
      this.locationsSubject.next(sorted);
    }, err => {
      console.warn('Error obtaining coords for sorting', err);
    });
  }




  writeValue(obj: any): void {
    this.value = obj;
    if (obj) {
      this.dataService.getCityById(obj).subscribe(location => {
        this.selectedLocation = location;
        if (this.selectedLocation) {
          this.citySection.emit(this.selectedLocation);
        }
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.locationsSubject.complete();
    if (this._dropdownHiddenHandler && this.dropDown?.nativeElement) {
      this.dropDown.nativeElement.removeEventListener('hidden.bs.dropdown', this._dropdownHiddenHandler);
    }
    this._dropdownHiddenHandler = undefined;
    this.unlockBodyScroll();
  }
  setLocation(location: City, locator: HTMLDivElement) {
    this.selectedLocation = location;
    this.value = location.id;
    this.onChange(location.id);
    this.onTouched();
    this.closeDropDown(locator);
    this.citySection.emit(location);
  }

  public closeDropDown(locator: HTMLDivElement) {
    const dropdown = bootstrap.Dropdown.getInstance(locator);
    dropdown?.hide();
    this.unlockBodyScroll();
  }

  public useCurrentLocation(locator: HTMLDivElement) {
    // Use shared GeolocationService which respects caching and timeouts.
    this.geo.getCoords(true).subscribe(coords => {
      if (!coords) {
        // Could not obtain coords (permission denied or timeout)
        console.warn('Geolocation unavailable or denied when using Use Current Location');
        return;
      }
      const { lat, lon } = coords;
      this.dataService.getNearestCity(lat, lon).subscribe(city => {
        if (!city) {
          console.warn('No nearest city returned from API for coords', coords);
          return;
        }
        this.setLocation(city, locator);
      }, err => {
        console.warn('Error fetching nearest city', err);
      });
    });
  }

  // cleaned up unused helper

}

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  if ([lat1, lon1, lat2, lon2].some(v => typeof v !== 'number' || !isFinite(v) || Number.isNaN(v))) return NaN;
  const toRad = (v: number) => v * Math.PI / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
